import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { capitalize } from '@crazyegginc/hatch';
import classNames from 'classnames';

import { Header } from '../components/ui/Header';

import BlueSpinnerIcon from '@crazyegginc/hatch/dist/animations/spinner-blue.svg';
import WorkFromHomeIllustrationIcon from '@crazyegginc/hatch/dist/images/illustration-work-from-home.svg';
import { techRecommendationQuery } from '../../installation/queries';
import { TECH_RECOMMENDATIONS, normalizeTechRecommendation } from '../constants';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';
import { useAuthContext } from '/src/hooks';
import { useOnboardingContext } from '../hooks/useOnboardingContext';

const MIN_CHECKING_MS = 3000;
const DETECTION_MESSAGE_MS = 1000;

const humanizeOnboardingTechnology = (tech) => {
  switch (tech.toLowerCase()) {
    case 'wordpress':
      return 'WordPress site';
    case 'gtm':
      return 'Google Tag Manager';
    case 'magento':
      return 'Magento 1';
    case 'magento2':
      return 'Magento 2';
    default:
      return capitalize(tech);
  }
};

export function CheckSiteStep() {
  const { currentUser } = useAuthContext();
  const mountedAtRef = useRef(Date.now());
  const [hasFakeTimerFinished, setHasFakeTimerFinished] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    siteUrl,
    heatmapUrl,
    recommendation: recommendationParam,
    isHeatmapFlow,
  } = useOnboardingContext(currentUser.onboarding.flow);
  const effectiveSiteUrl = siteUrl || heatmapUrl;
  const metadataRequestUrl = effectiveSiteUrl ? `https://${effectiveSiteUrl}` : undefined;
  const recommendation = normalizeTechRecommendation(recommendationParam);

  const { data: recommendationData, isFetched } = useQuery({
    ...techRecommendationQuery({ url: metadataRequestUrl }),
    enabled: Boolean(effectiveSiteUrl),
  });

  const fetchedRecommendationMethod = recommendationData?.techRecommendation?.method ?? null;
  const fetchedRecommendation = normalizeTechRecommendation(fetchedRecommendationMethod);

  useEffect(() => {
    const nextRecommendation = fetchedRecommendation ?? recommendation;
    const hasDetectedRecommendation = Boolean(nextRecommendation);
    const shouldNavigate = effectiveSiteUrl ? isFetched : true;

    if (shouldNavigate) {
      setHasFakeTimerFinished(false);

      const nextSearchParams = new URLSearchParams(searchParams);

      if (effectiveSiteUrl) {
        nextSearchParams.set('site_url', effectiveSiteUrl);
      } else {
        nextSearchParams.delete('site_url');
      }

      if (isHeatmapFlow) {
        const nextHeatmapUrl = heatmapUrl || effectiveSiteUrl;

        if (nextHeatmapUrl) {
          nextSearchParams.set('heatmap_url', nextHeatmapUrl);
        } else {
          nextSearchParams.delete('heatmap_url');
        }
      }

      if (nextRecommendation) {
        nextSearchParams.set('recommendation', nextRecommendation);
      } else {
        nextSearchParams.set('recommendation', TECH_RECOMMENDATIONS.NONE);
      }

      if (!effectiveSiteUrl) {
        navigate(
          {
            pathname: '/onboarding/instructions',
            search: nextSearchParams.toString(),
          },
          { replace: true },
        );

        return;
      }

      const elapsedTime = Date.now() - mountedAtRef.current;
      const initialDelay = Math.max(0, MIN_CHECKING_MS - elapsedTime);
      let secondTimeoutId;
      const firstTimeoutId = setTimeout(() => {
        if (hasDetectedRecommendation) {
          setHasFakeTimerFinished(true);

          secondTimeoutId = setTimeout(() => {
            navigate(
              {
                pathname: '/onboarding/instructions',
                search: nextSearchParams.toString(),
              },
              { replace: true },
            );
          }, DETECTION_MESSAGE_MS);

          return;
        }

        navigate(
          {
            pathname: '/onboarding/instructions',
            search: nextSearchParams.toString(),
          },
          { replace: true },
        );
      }, initialDelay);

      return () => {
        clearTimeout(firstTimeoutId);

        if (secondTimeoutId) {
          clearTimeout(secondTimeoutId);
        }
      };
    }
  }, [
    effectiveSiteUrl,
    fetchedRecommendation,
    heatmapUrl,
    isFetched,
    isHeatmapFlow,
    navigate,
    recommendation,
    searchParams,
  ]);

  const detectedTech = hasFakeTimerFinished ? (fetchedRecommendation ?? recommendation) : null;

  return (
    <>
      <Header currentStep={1} totalSteps={3} />

      <div
        className="mx-auto flex w-full max-w-[720px] flex-col gap-10 p-6 sm:py-10 sm:px-6 md:px-0"
        data-detected-tech={detectedTech ?? ''}
      >
        <img
          src={WorkFromHomeIllustrationIcon}
          alt="Work from home illustration"
          className="h-auto w-full max-w-[418px] self-center"
        />

        <div className="flex w-full items-center gap-4 self-center justify-start sm:justify-center">
          <div className="rounded-full shadow w-[48px] h-[49px] flex items-center justify-center flex-shrink-0">
            {detectedTech ? (
              <TickIcon className="w-5 h-5 text-dodger-blue-500 fill-current" />
            ) : (
              <img src={BlueSpinnerIcon} alt="Loading spinner" className="w-8 h-8 -my-px" />
            )}
          </div>

          <span
            className={classNames(
              `break-words text-2xl font-bold leading-9 text-black-pearl-500 md:text-[32px] md:leading-[48px]`,
              {
                'text-lynch-500': detectedTech,
              },
            )}
          >
            {detectedTech ? `Found ${humanizeOnboardingTechnology(detectedTech)}` : `Checking ${effectiveSiteUrl}...`}
          </span>
        </div>
      </div>
    </>
  );
}
